/**
 * Milestone view helpers — mirrors customer Load Tracker stage language
 * from backend/src/orders/public/orderTrackingFormatter.js (ADR-003).
 *
 * Frontend port of resolveCurrentStepKey + stage titles so staff and
 * customers share the same milestone vocabulary without importing Node modules.
 */

const ORDER_STATUS_RANK = {
  pending: 0,
  booked: 1,
  in_progress: 2,
  arrived_at_storage: 3,
  in_transit_from_storage: 4,
  completed: 5,
  cancelled: -1,
};

const STAGE_TITLES = {
  booking_confirmed: "Booking confirmed",
  pickup_scheduled: "Pickup scheduled",
  load_collected: "Load collected",
  at_storage: "At storage",
  trip_scheduled: "Departure scheduled",
  loaded: "Loaded for departure",
  in_transit: "In transit",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

/**
 * Same resolution rules as backend resolveCurrentStepKey.
 * @param {Object} order
 * @param {Object|null} [trip]
 * @returns {string}
 */
export function resolveCurrentStepKey(order, trip = null) {
  if (!order) return "booking_confirmed";
  if (order.status === "cancelled") return "cancelled";
  if (order.status === "completed") return "delivered";

  const tripStatus = trip?.status;
  if (order.status === "in_transit_from_storage" || tripStatus === "en_route") {
    return "in_transit";
  }
  if (order.status === "arrived_at_storage") return "at_storage";
  if (tripStatus === "loaded") return "loaded";
  if (trip && tripStatus === "planned") return "trip_scheduled";
  if (order.status === "in_progress") return "load_collected";
  if (order.booking?.moveDate) return "pickup_scheduled";
  if (order.status === "booked" || order.booking?.status === "confirmed") {
    return "booking_confirmed";
  }
  return "booking_confirmed";
}

/**
 * Build stage definitions in the same order as the public tracker.
 * @param {Object} order
 * @param {Object|null} [trip]
 * @returns {Array<{ key: string, title: string }>}
 */
export function buildMilestoneDefinitions(order, trip = null) {
  const status = order?.status;
  const showStorage =
    status === "arrived_at_storage" ||
    status === "in_transit_from_storage" ||
    (ORDER_STATUS_RANK[status] ?? 0) >= ORDER_STATUS_RANK.arrived_at_storage;

  const definitions = [
    { key: "booking_confirmed", title: STAGE_TITLES.booking_confirmed },
    { key: "pickup_scheduled", title: STAGE_TITLES.pickup_scheduled },
    { key: "load_collected", title: STAGE_TITLES.load_collected },
  ];

  if (showStorage) {
    definitions.push({ key: "at_storage", title: STAGE_TITLES.at_storage });
  }

  if (trip || order?.tripId) {
    definitions.push(
      { key: "trip_scheduled", title: STAGE_TITLES.trip_scheduled },
      { key: "loaded", title: STAGE_TITLES.loaded }
    );
  }

  definitions.push(
    { key: "in_transit", title: STAGE_TITLES.in_transit },
    { key: "delivered", title: STAGE_TITLES.delivered }
  );

  if (status === "cancelled") {
    definitions.push({ key: "cancelled", title: STAGE_TITLES.cancelled });
  }

  return definitions;
}

/**
 * @param {Object} order
 * @param {Object|null} [trip]
 * @returns {{
 *   current: { key: string, title: string }|null,
 *   next: { key: string, title: string }|null,
 *   stages: Array<{ key: string, title: string, state: "completed"|"current"|"upcoming"|"problem" }>,
 *   lastUpdatedAt: string|Date|null
 * }}
 */
export function getMilestoneView(order, trip = null) {
  const definitions = buildMilestoneDefinitions(order, trip);
  const currentKey = resolveCurrentStepKey(order, trip);
  const currentIndex = definitions.findIndex((step) => step.key === currentKey);

  const stages = definitions.map((step, index) => {
    let state = "upcoming";
    if (order?.status === "cancelled") {
      state = step.key === "cancelled" ? "current" : "upcoming";
    } else if (currentIndex < 0) {
      state = "upcoming";
    } else if (index < currentIndex) {
      state = "completed";
    } else if (index === currentIndex) {
      state = "current";
    }
    return { ...step, state };
  });

  const current =
    stages.find((s) => s.key === currentKey) ||
    stages.find((s) => s.state === "current") ||
    stages[0] ||
    null;
  const next =
    currentIndex >= 0 && currentIndex < stages.length - 1
      ? stages[currentIndex + 1]
      : null;

  return {
    current: current
      ? { key: current.key, title: current.title }
      : null,
    next: next ? { key: next.key, title: next.title } : null,
    stages,
    lastUpdatedAt: order?.updatedAt || order?.createdAt || null,
  };
}

export { STAGE_TITLES };
